import { EditorState, Transaction } from 'prosemirror-state'
import { Node as ProseNode } from 'prosemirror-model'
import { preserveSelection } from 'common/Prose/utils'

export const upsertBox = (state: EditorState, boxColor?: string, quoteColor?: string) => {
  const { tr, doc } = state

  const {
    selection: { $from, $to },
  } = tr

  const start = $from.before(1)
  const end = $to.after(1)

  const res = doc.resolve(start)
  const { nodeAfter } = res

  if (nodeAfter && nodeAfter.type.name === 'box' && start + nodeAfter.nodeSize === end) {
    // Already inside a box, so update the color
    tr.setNodeMarkup(start, undefined, {
      ...nodeAfter.attrs,
      boxColor: boxColor || nodeAfter.attrs.boxColor,
      quoteColor: quoteColor || nodeAfter.attrs.quoteColor,
    })
  } else {
    // Otherwise, remove any existing boxes and wrap in a new one
    removeFromBox(state, tr, { box: true, quote: true })
    wrapInBox(state, tr, { boxColor, quoteColor })
  }
  return tr
}

const wrapInBox = (state: EditorState, tr: Transaction, attrs?: {}) => {
  const { selection } = tr
  const { $from, $to } = selection

  const start = $from.before(1)
  const end = $to.after(1)

  const fragment = state.doc.slice(start, end).content

  if (fragment.size > 0) {
    tr.replaceRangeWith(start, end, state.schema.nodes.box.create(attrs, fragment))
    // +1 offset is because we are adding the box as a wrapper node, so the new selection
    // positions need a +1 offset compared to the original selection
    preserveSelection(tr, state, 1)
  }

  return tr
}

export const removeFromBox = (
  state: EditorState,
  tr: Transaction,
  { box, quote }: { box?: boolean; quote?: boolean },
) => {
  let nodesFound = true
  let removed = false

  while (nodesFound) {
    const {
      selection: { $from, $to },
      doc,
    } = tr

    const start = $from.before(1)
    const end = $to.after(1)

    removed = false

    const removeBox = (node: ProseNode, beforePos: number) => {
      // replace box by it's content is effectively removing the box
      tr.replaceWith(beforePos, beforePos + node.nodeSize, node.content)
      // -1 offset is because we are removing the box, so the new selection positions
      // need a -1 offset compared to the original selection
      preserveSelection(tr, state, -1)
    }

    doc.nodesBetween(start, end, (node, pos) => {
      if (node.type === state.schema.nodes.box && !removed) {
        if (box) {
          // if we want to remove boxColor...
          if (node.attrs.quoteColor) {
            // ... and node has quoteColor, then only remove the boxColor attribute from node
            const { boxColor, ...remainingAttrs } = node.attrs
            tr.setNodeMarkup(pos, undefined, remainingAttrs)
          } else {
            // ... else remove the box entirely
            removeBox(node, pos)
            removed = true
          }
        }

        if (quote) {
          // if we want to remove quoteColor...
          if (node.attrs.boxColor) {
            // ... and node has boxColor, then only remove the quoteColor attribute from node
            const { quoteColor, ...remainingAttrs } = node.attrs
            tr.setNodeMarkup(pos, undefined, remainingAttrs)
          } else {
            // ... else remove the box entirely
            removeBox(node, pos)
            removed = true
          }
        }
      }
    })

    // If no nodes were found, exit the loop
    if (!removed) nodesFound = false
  }
}
